import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { useAuth } from '../../../contexts/AuthContext';
import { useData } from '../../../contexts/DataContext';
import { exportToPDF } from '../../../utils/exportUtils';

const { FiShield, FiCheckCircle, FiDownload, FiFileText, FiAlertTriangle, FiCheck } = FiIcons;

const DataProcessingAgreement = () => {
  const { user } = useAuth();
  const { companies, updateCompany } = useData();
  const [isSigning, setIsSigning] = useState(false);
  
  const company = companies.find(c => c.id === user?.companyId);
  const isSigned = company?.dpaAccepted || false;
  const signedDate = company?.dpaAcceptedDate ? new Date(company.dpaAcceptedDate).toLocaleDateString('nb-NO') : null;

  // Standard DPA Text (Simplified for the template)
  const dpaText = `
    DATABEHANDLERAVTALE

    Mellom
    Behandlingsansvarlig: ${company?.name || '[Bedriftsnavn]'} (Kunden)
    og
    Databehandler: Employer Brand AS (Leverandøren)

    1. FORMÅL OG OMFANG
    Denne avtalen regulerer Databehandlerens behandling av personopplysninger på vegne av den Behandlingsansvarlige i forbindelse med leveranse av tjenesten HMSvarsling.

    2. BEHANDLINGENS ART OG FORMÅL
    Formålet med behandlingen er å tilby en digital varslingskanal og HMS-system.
    Behandlingen omfatter innsamling, lagring, sammenstilling og sletting av personopplysninger knyttet til varslingssaker og HMS-hendelser.

    3. KATEGORIER AV REGISTRERTE OG PERSONOPPLYSNINGER
    Registrerte: Ansatte, innleide konsulenter, leverandører.
    Opplysninger: Navn, kontaktinfo, beskrivelse av hendelser, IP-adresser, logger. Særlige kategorier (sensitive data) kan forekomme i fritekstfelt.

    4. DATABEHANDLERENS PLIKTER
    Databehandleren skal kun behandle personopplysninger basert på dokumenterte instrukser fra den Behandlingsansvarlige.
    Databehandleren skal sikre at personer som har tilgang til opplysningene har taushetsplikt.
    Databehandleren skal gjennomføre egnede tekniske og organisatoriske sikkerhetstiltak (kryptering, tilgangsstyring, logging).

    5. BRUK AV UNDERLEVERANDØRER
    Databehandleren benytter følgende underleverandører:
    - Supabase (Database hosting, EU/Frankfurt)
    - Stripe (Betalingsformidling)
    - EmailJS (E-posttjenester)
    Ved bytte av underleverandører skal Kunden varsles.

    6. BISTAND TIL DEN BEHANDLINGSANSVARLIGE
    Databehandleren skal bistå Kunden med å oppfylle pliktene i GDPR (innsyn, sletting, retting) og ved eventuelle avviksmeldinger til Datatilsynet.

    7. SLETTING OG TILBAKEFØRING
    Ved opphør av tjenesteavtalen skal alle data slettes eller tilbakeføres til Kunden innen 90 dager, med mindre lovverk krever videre lagring.

    Denne avtalen er elektronisk signert og gyldig så lenge hovedavtalen løper.
  `;

  const handleSign = async () => {
    setIsSigning(true);
    // Simulate API call
    setTimeout(async () => {
      await updateCompany(company.id, {
        dpaAccepted: true,
        dpaAcceptedDate: new Date().toISOString(),
        dpaVersion: '1.0'
      });
      setIsSigning(false);
    }, 1000);
  };

  const handleDownload = () => {
    const data = {
      title: 'Databehandleravtale',
      description: dpaText,
      id: `DPA-${company.orgNumber || 'UKJENT'}`,
      status: isSigned ? `Signert av ${user.name} den ${signedDate}` : 'Utkast',
      type: 'Juridisk Dokument',
      companyName: company.name
    };
    exportToPDF(data, 'generic'); // Uses generic export logic
  };

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
        <div className="flex items-start gap-3">
          <SafeIcon icon={FiShield} className="text-blue-600 text-xl mt-1" />
          <div>
            <h3 className="font-bold text-blue-900">Lovpålagt Avtale (GDPR Art. 28)</h3>
            <p className="text-sm text-blue-800 mt-1">
              Siden HMSvarsling behandler personopplysninger på vegne av din bedrift, er vi lovpålagt å inngå en Databehandleravtale. 
              Denne sikrer at vi kun behandler data etter dine instrukser og med høy sikkerhet.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        {/* Status Header */}
        <div className={`p-4 border-b flex justify-between items-center ${isSigned ? 'bg-green-50 border-green-100' : 'bg-gray-50 border-gray-200'}`}>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-full ${isSigned ? 'bg-green-100 text-green-600' : 'bg-gray-200 text-gray-500'}`}>
              <SafeIcon icon={isSigned ? FiCheckCircle : FiFileText} className="text-xl" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Status: {isSigned ? 'Signert og Gyldig' : 'Mangler signatur'}</h3>
              {isSigned && <p className="text-xs text-green-700">Akseptert den {signedDate} av {user.name}</p>}
            </div>
          </div>
          <button 
            onClick={handleDownload}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors text-gray-700"
          >
            <SafeIcon icon={FiDownload} /> Last ned PDF
          </button>
        </div>

        {/* Agreement Text Preview */}
        <div className="p-6 bg-gray-50/50">
          <div className="bg-white border border-gray-200 p-6 rounded-lg shadow-inner h-96 overflow-y-auto font-mono text-sm text-gray-700 whitespace-pre-wrap">
            {dpaText}
          </div>
        </div>

        {/* Action Footer */}
        <div className="p-6 border-t border-gray-200 bg-white">
          {!isSigned ? (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 px-3 py-2 rounded-lg border border-amber-100">
                <SafeIcon icon={FiAlertTriangle} />
                <span>Avtalen må signeres for å oppfylle lovkrav.</span>
              </div>
              <button
                onClick={handleSign}
                disabled={isSigning}
                className="w-full sm:w-auto px-6 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 shadow-lg"
              >
                {isSigning ? 'Signerer...' : 'Jeg aksepterer avtalen'}
                {!isSigning && <SafeIcon icon={FiCheck} />}
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-center sm:justify-start gap-2 text-sm text-green-700 font-medium">
              <SafeIcon icon={FiCheckCircle} />
              Avtalen er aktiv. Ingen handling kreves.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataProcessingAgreement;